import type { GetServerSideProps, GetServerSidePropsResult } from "next";
import type { IConfig } from "config/IConfig";
import { ServiceProvider } from "contexts/ServiceContext";
import { ProfilePageTypes } from "enums/ProfilePageTypes";
import useCreateServices from "hooks/useCreateServices";
import type { ITexts } from "translations/ITexts";
import type { IRequestNeededForServices } from "types/IRequestNeededForServices";
import { getServerSidePropertiesNeededForEveryPage } from "utils/getRequestNeededForServicesFromContext";
import useInitAnalytics from "hooks/useInitAnalytics";
import { QueryClient } from "react-query";
import { dehydrate, DehydratedState } from "react-query/hydration";
import { PROFILE_QUERY_KEY } from "queries/queryKeys";
import createServices from "services/createServices";
import { EMBEDDED_WEBVIEW_COOKIE } from "cookies/cookiesConstants";
import { useEffect } from "react";
import NotificationsManagementContainer from "components/Notifications/NotificationsManagementContainer";
import {
    GetNotificationsByHashQuery,
    GetNotificationsByHashQueryVariables,
} from "graphql/schema-types";
import getNotificationsQuery from "services/gql/queries/getNotificationsQuery";
import NoResultsContainer from "components/Notifications/NoResultsContainer";

interface IProps {
    configs: IConfig;
    texts: ITexts;
    req: IRequestNeededForServices;
    queryResult: GetNotificationsByHashQuery;
    hash: string;
}

export default function NotificationsManagement({
    configs,
    req,
    texts,
    queryResult,
    hash,
}: IProps) {
    const services = useCreateServices(configs, texts, ProfilePageTypes.ALERT_MANAGER, req);

    useInitAnalytics(services);

    useEffect(
        () => {
            services.TiendeoStatsClient.Navigation(0);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    if (queryResult === null) {
        return (
            <ServiceProvider Services={services}>
                <NoResultsContainer />
            </ServiceProvider>
        );
    }

    return (
        <ServiceProvider Services={services}>
            <NotificationsManagementContainer queryResult={queryResult} hash={hash} />
        </ServiceProvider>
    );
}

export const getServerSideProps: GetServerSideProps<IProps> = async (context) => {
    const { configs, req, texts, res } = getServerSidePropertiesNeededForEveryPage(context);

    const { GqlService, Logger } = createServices(
        configs,
        texts,
        ProfilePageTypes.LOYALTY_CARDS,
        req as any,
        res,
    );
    const hash = context.query.hash as string;

    let queryResult: GetNotificationsByHashQuery;

    try {
        queryResult = await GqlService.request<
            GetNotificationsByHashQuery,
            GetNotificationsByHashQueryVariables
        >(getNotificationsQuery, { hash: hash });
    } catch (error) {
        Logger.error(error);
        console.log("ERROR: ", error);
        queryResult = null;
    } finally {
        return {
            props: {
                configs,
                req,
                texts,
                queryResult,
                hash,
            },
        };
    }
};
